/**
 * lagMonitor — 프레임 스파이크(렉) 자동 감지 + 로그 콜백 순수 코어. Phaser/DOM 의존 0.
 *
 * frameMs가 임계(기본 50ms=20fps↓)를 넘으면 onLag(컨텍스트)를 호출한다. 연속 스파이크가
 * 로그를 쏟지 않게 debounce(기본 1초)로 억제한다. 시간은 frameMs 누적(performance.now 미사용 → 결정론).
 * idleblade엔 없던 신규 — Unity LogCollector 메모리 스냅샷 + 저메모리 핸들러 정신을 렉 영역으로 확장.
 */

export interface LagContext {
  /** 렉 프레임 간격 ms. */
  frameMs: number;
  /** 그 시점 fps(=1000/frameMs 근사). */
  fps: number;
  /** 섹션별 처리시간 ms (어느 구간이 느렸나). */
  sections: Record<string, number>;
  /** 객체 수 (proj/brick/tween 등). */
  counts: Record<string, number>;
}

export interface LagMonitorOptions {
  /** 렉 판정 프레임 간격 ms (기본 50 = 20fps). */
  lagThresholdMs?: number;
  /** 로그 억제 간격 ms (기본 1000) — 직전 로그 후 이 시간 안의 추가 렉은 무시. */
  debounceMs?: number;
  /** 렉 발생 시 호출 — 앱이 console.warn/diag-log로 출력. */
  onLag: (ctx: LagContext) => void;
}

export interface LagMonitor {
  /**
   * 매 프레임 호출. frameMs가 임계 초과 + debounce 충족 시에만 contextFn을 평가해 onLag 호출.
   * contextFn은 lazy — 렉이 아니면 평가하지 않아 평시 비용 0.
   */
  observe(frameMs: number, contextFn: () => Omit<LagContext, "frameMs">): void;
}

export function createLagMonitor(opts: LagMonitorOptions): LagMonitor {
  const threshold = opts.lagThresholdMs ?? 50;
  const debounce = opts.debounceMs ?? 1000;
  let sinceLastLog = debounce; // 첫 렉은 즉시 로그 가능하게 debounce를 채워 시작.

  function observe(frameMs: number, contextFn: () => Omit<LagContext, "frameMs">): void {
    sinceLastLog += frameMs;
    if (frameMs > threshold && sinceLastLog >= debounce) {
      sinceLastLog = 0;
      opts.onLag({ frameMs, ...contextFn() });
    }
  }

  return { observe };
}
